import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box, Button } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getAllElectronicInvoiceAddresses, hasLinkedEInvoiceAddress } from 'src/shared/services/electronicInvoiceAddressService';
import { toast } from 'react-toastify';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { EInvoiceSettingsModal } from 'src/modules/member/components/eInvoiceSettingsModal';
import { Member } from 'src/modules/member/models/member';
import { ElectronicInvoiceAddress } from '../models/electronicInvoiceAddress';
import { EInvoiceAddressSearchFilter } from '../models/eInvoiceAddressSearchFilter';

type EInvoiceApplicationsDataGridProps = {
  searchFilter: EInvoiceAddressSearchFilter;
  searchFilterCallback: (filter: EInvoiceAddressSearchFilter) => void;
  onApplicationLinked?: () => void;
};

export const EInvoiceApplicationsDataGrid = (props: EInvoiceApplicationsDataGridProps) => {
  const { searchFilter, searchFilterCallback, onApplicationLinked } = props;
  const { t } = useTranslation(['invoice', 'member', 'common']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'));

  const [selectedApplication, setSelectedApplication] = useState<ElectronicInvoiceAddress | undefined>(undefined);
  const [rows, setRows] = useState<ElectronicInvoiceAddress[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    try {
      const result = await getAllElectronicInvoiceAddresses(searchFilter, true);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.eInvoiceAddress_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const columns: GridColDef[] = [
    {
      field: 'eInvoiceAddress_id',
      headerName: 'ID',
      sortable: true,
      flex: 1,
    },
    {
      field: 'firstName',
      headerName: t('invoice:eInvoice.address.tableHeader.firstName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:eInvoice.address.tableHeader.lastName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.lastName}</>;
      },
    },
    {
      field: 'memberNr',
      headerName: t('invoice:eInvoice.applications.tableHeader.nr'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.memberNr || ''}</>;
      },
    },
    {
      field: 'openMember',
      headerName: '',
      sortable: false,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const memberId = params.row.member_id;
        const memberIsOther = params.row.member?.other === true;
        const route = memberIsOther ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails;
        return (
          <Button
            variant='outlined'
            size='small'
            endIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => window.open(`${route}/${memberId}`, '_blank')}
          >
            {t('common:button.open')}
          </Button>
        );
      },
    },
    {
      field: 'type',
      headerName: t('invoice:eInvoice.applications.tableHeader.type'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.other === true ? otherLabel : t('member:memberLabel')}</>;
      },
    },
    {
      field: 'orgNr',
      headerName: t('invoice:eInvoice.applications.tableHeader.orgNr'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.orgNr}</>;
      },
    },
    {
      field: 'gln',
      headerName: t('invoice:eInvoice.applications.tableHeader.gln'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.gln}</>;
      },
    },
    {
      field: 'buyerReference',
      headerName: t('invoice:eInvoice.address.tableHeader.reference'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.buyerReference}</>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const handleLink = async () => {
          const application = params.row as ElectronicInvoiceAddress;
          try {
            const alreadyLinked = await hasLinkedEInvoiceAddress(application.member_id);
            if (alreadyLinked) {
              toast.error(<>{t('invoice:eInvoice.applications.alreadyLinked')}</>);
              return;
            }
          } catch {
            // If check fails, allow proceeding
          }
          setSelectedApplication(application);
        };
        return (
          <Button variant='contained' size='small' onClick={handleLink}>
            {t('invoice:eInvoice.applications.link')}
          </Button>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<ElectronicInvoiceAddress>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        columnVisibilityModel={{ eInvoiceAddress_id: false }}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
      {selectedApplication && (
        <EInvoiceSettingsModal
          open={!!selectedApplication}
          onClose={() => setSelectedApplication(undefined)}
          member={
            {
              member_id: selectedApplication.member_id,
              memberNr: selectedApplication.member?.memberNr,
              firstName: selectedApplication.member?.firstName || '',
              lastName: selectedApplication.member?.lastName || '',
            } as Member
          }
          refetchMemberCallback={() => {
            setSelectedApplication(undefined);
            fetchData();
            onApplicationLinked?.();
          }}
          initialCinSearch={selectedApplication.orgNr || undefined}
          existingAddressId={selectedApplication.eInvoiceAddress_id}
          initialBuyerReference={selectedApplication.buyerReference || undefined}
        />
      )}
    </Box>
  );
};
